import React from 'react'
import type { FieldValues, Path, Control } from 'react-hook-form'

import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
} from 'src/shared/components/ui/forms/form'
import type { PlaceResult } from 'src/shared/components/ui/forms/location-autocomplete'
import { LocationAutoComplete } from 'src/shared/components/ui/forms/location-autocomplete'
import { cn } from 'src/shared/utils/cn'

interface LocationAutocompleteFieldProps {
  _field: any
  label?: string
  handlePlaceSelected?: (place: PlaceResult) => void
  locationTypes?: string[]
  locationRefValue?: 'street' | 'city' | ''
  floatingLabel?: boolean
  displayValue?: string
}

const LocationAutocompleteField: React.FC<LocationAutocompleteFieldProps> = ({
  _field,
  label,
  handlePlaceSelected,
  locationTypes,
  locationRefValue,
  displayValue,
}) => {
  if (!handlePlaceSelected) return null

  return (
    <LocationAutoComplete
      onPlaceSelected={handlePlaceSelected}
      placeholder={`${label}`}
      componentRestrictions={{ country: 'us' }}
      types={locationTypes || ['address']}
      refValue={locationRefValue || 'street'}
      value={displayValue}
    />
  )
}

interface LocationAutocompleteFormFieldProps<T extends FieldValues> {
  control?: Control<T>
  name: Path<T>
  label?: string
  description?: string
  className?: string
  fieldClassName?: string
  handlePlaceSelected?: (place: PlaceResult) => void
  locationTypes?: string[]
  locationRefValue?: 'street' | 'city' | ''
  required?: boolean
  floatingLabel?: boolean
  value?: string
}

export function LocationAutocompleteFormField<T extends FieldValues>({
  control,
  name,
  label,
  description,
  className,
  fieldClassName,
  handlePlaceSelected,
  locationTypes,
  locationRefValue,
  required = false,
  floatingLabel = true,
  value,
}: LocationAutocompleteFormFieldProps<T>) {
  if (required) {
    label = `${label} *`
  }

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={cn('w-full', className)}>
          {label && <FormLabel className={floatingLabel ? 'sr-only' : ''}>{label}</FormLabel>}
          <FormControl>
            <div className={cn('relative', fieldClassName)}>
              <LocationAutocompleteField
                _field={field}
                label={label}
                handlePlaceSelected={handlePlaceSelected}
                locationTypes={locationTypes}
                locationRefValue={locationRefValue}
                displayValue={value}
              />
            </div>
          </FormControl>
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  )
}
